import React, { useState } from "react";
import { Phone, MessageCircle, ShieldCheck, ArrowLeft } from "lucide-react";
import { Btn } from "./ui";
import { api } from "../api/client";

// Confirmação de telefone por código no WhatsApp — o miolo compartilhado.
//
// Regra do dono (09/09/2026): "qualquer parte que for configurar o telefone
// tem que confirmar o telefone, senão pessoas vão colocar números que não são
// delas". O número recebe 6 dígitos NO PRÓPRIO WhatsApp e só vale depois de
// digitado de volta.
//
// Usado por: ModalTelefoneObrigatorio (portão de quem não tem número),
// ModalAlterarTelefone (troca) e os cadastros de empresa/locadora
// (destino="comercial", que confirma o número sem mexer no telefone da conta).
//
// Se o backend responder `verificacao_dispensada` (interruptor desligado
// porque o template do Twilio ainda não existe), o número é salvo direto e o
// passo do código é PULADO — ninguém fica preso esperando um código que não
// vai chegar.

export const mascararTelefone = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return d.replace(/(\d{2})(\d+)/, "($1) $2");
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d+)/, "($1) $2-$3");
  return d.replace(/(\d{2})(\d{5})(\d+)/, "($1) $2-$3");
};

// Celular BR: DDD + 9 dígitos começando em 9. Fixo não recebe WhatsApp.
export const ehCelular = (v) => {
  const d = String(v || "").replace(/\D/g, "");
  return d.length === 11 && d[2] === "9";
};

export const VerificarTelefoneWhatsApp = ({
  destino = "conta",
  telefoneInicial = "",
  onConfirmado,
  onCancelar,
  textoBotao = "Enviar código",
}) => {
  const [passo, setPasso] = useState("numero"); // numero → codigo
  const [telefone, setTelefone] = useState(mascararTelefone(telefoneInicial));
  const [codigo, setCodigo] = useState("");
  const [mascarado, setMascarado] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");

  const digitos = telefone.replace(/\D/g, "");

  const enviarCodigo = async () => {
    if (!ehCelular(digitos)) {
      setErro("Informe um celular com WhatsApp: DDD + 9 dígitos.");
      return;
    }
    setEnviando(true);
    setErro("");
    try {
      const r = await api.fetch("/usuarios/me/telefone/enviar-codigo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ telefone: digitos, destino }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Não foi possível enviar o código.");
      // Interruptor desligado ou número que esta conta já confirmou antes:
      // nada de código, o número já vale.
      if (j.verificacao_dispensada || j.ja_verificado) {
        onConfirmado && onConfirmado(j.telefone || digitos);
        return;
      }
      setMascarado(j.telefone_mascarado || mascararTelefone(digitos));
      setCodigo("");
      setPasso("codigo");
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const confirmar = async () => {
    if (!/^\d{6}$/.test(codigo.trim())) {
      setErro("O código tem 6 dígitos.");
      return;
    }
    setEnviando(true);
    setErro("");
    try {
      const r = await api.fetch("/usuarios/me/telefone/confirmar-codigo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo: codigo.trim(), destino }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Código inválido.");
      onConfirmado && onConfirmado(j.telefone || digitos);
    } catch (e) {
      setErro(e.message);
    } finally {
      setEnviando(false);
    }
  };

  if (passo === "codigo") {
    return (
      <div className="space-y-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-800 flex gap-2">
          <MessageCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>
            Enviamos um código de 6 dígitos no WhatsApp <b>{mascarado}</b>. Ele vale por 10 minutos.
          </span>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Código recebido</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={codigo}
            autoFocus
            onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => { if (e.key === "Enter") confirmar(); }}
            placeholder="000000"
            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-2xl font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-orange-300"
          />
        </div>
        {erro && <div className="bg-red-50 border border-red-200 rounded-xl p-2 text-xs text-red-700">{erro}</div>}
        <Btn v="primary" full disabled={enviando} onClick={confirmar}>
          {enviando ? "Confirmando..." : "Confirmar número"}
        </Btn>
        <button
          onClick={() => { setPasso("numero"); setCodigo(""); setErro(""); }}
          className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 pt-1"
        >
          <ArrowLeft size={13} /> Corrigir número ou reenviar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex gap-2 text-xs text-orange-800">
        <ShieldCheck size={16} className="flex-shrink-0 mt-0.5" />
        <span>
          Vamos mandar um código no WhatsApp deste número para confirmar que ele é seu.
          Seu número <b>nunca</b> aparece para outros usuários.
        </span>
      </div>
      <div>
        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Celular com WhatsApp</label>
        <div className="relative">
          <span className="absolute left-3.5 top-3 text-slate-400"><Phone size={16} /></span>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            autoFocus
            value={telefone}
            onChange={(e) => setTelefone(mascararTelefone(e.target.value))}
            onKeyDown={(e) => { if (e.key === "Enter") enviarCodigo(); }}
            placeholder="(62) 99999-9999"
            className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none text-base md:text-sm"
          />
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5">
          Precisa ser um celular — telefone fixo não recebe mensagem no WhatsApp.
        </p>
      </div>
      {erro && <div className="bg-red-50 border border-red-200 rounded-xl p-2 text-xs text-red-700">{erro}</div>}
      <div className="flex gap-2">
        {onCancelar && <Btn v="ghost" onClick={onCancelar}>Cancelar</Btn>}
        <Btn v="primary" full disabled={enviando} onClick={enviarCodigo}>
          {enviando ? "Enviando..." : textoBotao}
        </Btn>
      </div>
    </div>
  );
};
